import { FormEvent, useEffect, useState } from "react";
import { fetchHealthCheck, HealthReport } from "../lib/healthcheck";

function normalizeDomain(input: string): string {
  let s = input.trim().toLowerCase().replace(/\.$/, "");
  s = s.replace(/^https?:\/\//, "").replace(/\/.*$/, "");
  return s;
}

const STATUS_LABEL: Record<string, string> = {
  ok: "OK",
  nxdomain: "None",
  timeout: "Timeout",
  error: "Error",
  servfail: "SERVFAIL",
};

export function DnsHealth() {
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<HealthReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = (raw: string) => {
    const d = normalizeDomain(raw);
    if (!d) return;
    setDomain(d);
    setBusy(true);
    setReport(null);
    setError(null);
    history.replaceState(null, "", `?${new URLSearchParams({ domain: d })}`);
    fetchHealthCheck(d)
      .then((r) => setReport(r))
      .catch((e) => setError(String(e?.message ?? e)))
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    const d = new URLSearchParams(window.location.search).get("domain");
    if (d) run(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    run(domain);
  };

  return (
    <main className="console">
      <div className="input-row" role="search">
        <span className="input-row-label">HEALTH</span>
        <form onSubmit={submit} className="lookup-form">
          <input
            type="text"
            placeholder="example.com"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            autoFocus
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            aria-label="Domain name"
          />
          <button type="submit" disabled={busy || !domain.trim()}>
            {busy ? "Checking…" : "Check"}
          </button>
        </form>
        <span className="input-row-hint" aria-hidden="true">
          <span className="stat-dot" /> all record types · one report
        </span>
      </div>

      {error ? <div className="error" role="alert">{error}</div> : null}

      <div className="body-grid body-grid-single">
        <section className="table-panel">
          <div className="panel-title">
            DNS HEALTH REPORT
            {report ? <b> · {report.domain}</b> : null}
          </div>

          {!report && !busy ? (
            <div className="empty-state">
              <p>Enter a domain for a one-shot report card across all common DNS record types — A, AAAA, MX, NS, TXT, SOA, CAA — plus plain-English findings.</p>
              <p className="empty-hint">Try <code>example.com</code> or your own domain.</p>
            </div>
          ) : null}

          {busy ? <div className="empty-state"><p>Running checks…</p></div> : null}

          {report ? (
            <>
              <ul className="health-findings">
                {report.findings.map((f, i) => (
                  <li key={i} className={`finding finding-${f.level}`}>
                    <span className="finding-icon" aria-hidden="true">
                      {f.level === "good" ? "✓" : f.level === "warn" ? "!" : "·"}
                    </span>
                    {f.text}
                  </li>
                ))}
              </ul>

              <table className="health-table">
                <thead>
                  <tr><th>Type</th><th>Status</th><th>Records</th></tr>
                </thead>
                <tbody>
                  {report.entries.map((e) => (
                    <tr key={e.type} className={`health-row health-${e.status}`}>
                      <td className="ht-type"><code>{e.type}</code></td>
                      <td className="ht-status">{STATUS_LABEL[e.status] ?? e.status}</td>
                      <td className="ht-records">
                        {e.records.length ? (
                          <ul>
                            {e.records.map((r, i) => <li key={i}><code>{r}</code></li>)}
                          </ul>
                        ) : (
                          <span className="ht-empty">{e.message ?? "—"}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <p className="whois-cross">
                Check propagation across 14 resolvers:{" "}
                <a href={`/?host=${encodeURIComponent(report.domain)}&type=A`}>A record</a> ·{" "}
                <a href={`/?host=${encodeURIComponent(report.domain)}&type=MX`}>MX</a> · or its{" "}
                <a href={`/whois-lookup?domain=${encodeURIComponent(report.domain)}`}>WHOIS record</a>.
              </p>
            </>
          ) : null}
        </section>
      </div>

    </main>
  );
}
